import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Company, CompanyUser } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { FETCH_COMPANIES_QUERY_KEY } from './company-query-keys';
import { addOrUpdateCompanyCache } from './company-cache';

export function useFetchCompanies(): {
    status: 'error' | 'success' | 'pending';
    companies: Company[] | undefined;
    myUsers: CompanyUser[] | undefined;
    refetch: () => void;
} {
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    const { status, data, refetch } = useQuery({
        queryKey: [FETCH_COMPANIES_QUERY_KEY],
        queryFn: () => {
            return openApiRef.current.companies
                .getCompanies()
                .then((response) => {
                    const data = response.data.data;
                    data.companies.forEach((company) => {
                        addOrUpdateCompanyCache(queryClient, company);
                    });
                    // My own company user is a full record, so the per-user
                    // cache can be seeded from it. Written directly rather than
                    // through addOrUpdateCompanyUserCache, which writes back
                    // into this very query.
                    data.myUsers.forEach((user) => {
                        queryClient.setQueryData<CompanyUser>(
                            ['fetchCompanyUser', user.id],
                            () => user
                        );
                    });
                    return data;
                });
        }
    });

    return {
        status,
        companies: data?.companies,
        myUsers: data?.myUsers,
        refetch: () => {
            void refetch();
        }
    };
}

// Re-exported so existing import sites keep working; the constant lives in
// `company-query-keys` to keep the two service modules free of an import cycle.
